import React from 'react';
import { useTheme } from '../lib/themeContext';

interface Props {
  value: number;
  refLow: number | null;
  refHigh: number | null;
  optLow?: number;
  optHigh?: number;
  /** Color of the marker dot */
  markerColor: string;
}

/**
 * Horizontal range bar showing where a lab value falls relative to reference/optimal ranges.
 * Gray track, green reference zone, teal optimal zone, colored marker dot.
 */
export default function LabGauge({ value, refLow, refHigh, optLow, optHigh, markerColor }: Props) {
  const { isDark } = useTheme();
  if (refLow == null && refHigh == null) return null;

  const lo = refLow ?? 0;
  const hi = refHigh ?? lo + 100;
  // Visual range: pad 25% below/above reference so the marker stays visible
  const pad = (hi - lo) * 0.35;
  const visMin = Math.min(lo - pad, value * 0.7);
  const visMax = Math.max(hi + pad, value * 1.3);
  const span = visMax - visMin || 1;

  const toX = (v: number) => ((v - visMin) / span) * 100;

  const refX1 = Math.max(0, toX(lo));
  const refX2 = Math.min(100, toX(hi));
  const optX1 = optLow != null ? Math.max(0, toX(optLow)) : undefined;
  const optX2 = optHigh != null ? Math.min(100, toX(optHigh)) : undefined;
  const markerX = Math.max(1, Math.min(99, toX(value)));

  return (
    <div className="w-full mt-2 mb-1">
      <svg width="100%" height="14" viewBox="0 0 100 14" preserveAspectRatio="none">
        {/* Track */}
        <rect x={0} y={4} width={100} height={6} rx={3} fill={isDark ? '#374151' : '#E5E7EB'} />
        {/* Reference range */}
        <rect x={refX1} y={4} width={refX2 - refX1} height={6} rx={3} fill={isDark ? '#065F46' : '#D1FAE5'} />
        {/* Optimal range (darker green) */}
        {optX1 != null && optX2 != null && (
          <rect x={optX1} y={4} width={optX2 - optX1} height={6} rx={3} fill={isDark ? '#059669' : '#6EE7B7'} opacity={0.6} />
        )}
        {/* Value marker */}
        <circle cx={markerX} cy={7} r={4.5} fill={markerColor} stroke={isDark ? '#232B25' : 'white'} strokeWidth={1.5} />
      </svg>
    </div>
  );
}
